import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { styled } from "@mui/material/styles";
// components
import Iconify from "../components/iconify";
import QuarterlyStatsTable from "../components/table/QuarterlyStatsTable";

const StyledRoot = styled(Toolbar)(({ theme }) => ({
	height: 96,
	display: "flex",
	justifyContent: "start",
	gap: 16,
	padding: theme.spacing(0, 1, 0, 3),
}));

export default function QuarterlyReport() {
	const [year, setYear] = useState(new Date().getFullYear());
	const [quarter, setQuarter] = useState(1);
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const idToken = localStorage.getItem("idToken");

	const handleFetchReports = async () => {
		setLoading(true);
		const url = `/api/dashboard/quarterly-stats?year=${year}&quarter=${quarter}`;

		try {
			const response = await fetch(url, {
				headers: { Authorization: `Bearer ${idToken}` },
			});

			if (response.ok) {
				const result = await response.json();
				setData(result);
				setError(null);
			} else {
				setError("Failed to fetch quarterly reports.");
			}
		} catch (err) {
			setError("Error connecting to server.");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadExcel = async () => {
		setLoading(true);
		const idToken = localStorage.getItem("idToken");
		const url = `/api/dashboard/download-excel?year=${year}&quarter=${quarter}`;

		try {
			const response = await fetch(url, {
				method: "GET",
				headers: {
					"Authorization": `Bearer ${idToken}`,
					"Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				},
			});

			if (!response.ok) {
				throw new Error("Download failed");
			}

			const blob = await response.blob();
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.setAttribute("download", `Quarterly_Report_${year}_Q${quarter}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);

		} catch (err) {
			console.error(err);
			setError("Could not download Excel file. Please check your connection.");
		} finally {
			setLoading(false);
		}
	};

	// Change this part in your QuarterlyReport() function:
	const totalJoinedQ1 = data ? data.housingStats.reduce((acc, curr) => acc + (curr.joined_q1 || 0), 0) : 0;
	const totalJoinedQ2 = data ? data.housingStats.reduce((acc, curr) => acc + (curr.joined_q2 || 0), 0) : 0;
	const totalJoinedQ3 = data ? data.housingStats.reduce((acc, curr) => acc + (curr.joined_q3 || 0), 0) : 0;
	const totalJoinedQ4 = data ? data.housingStats.reduce((acc, curr) => acc + (curr.joined_q4 || 0), 0) : 0;

	// You can also create a helper for the current selected quarter's total
	const currentJoinedTotal = [totalJoinedQ1, totalJoinedQ2, totalJoinedQ3, totalJoinedQ4][quarter - 1];

	return (
		<>
			<Helmet><title> Quarterly Reports </title></Helmet>

			<Container maxWidth="xl">
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4">Quarterly Statistics</Typography>
				</Stack>

				<Card sx={{ mb: 3 }}>
					<StyledRoot>
						<FormControl sx={{ minWidth: 120 }}>
							<InputLabel>Financial Year</InputLabel>
							<Select value={year} label="Financial Year" onChange={(e) => setYear(e.target.value)}>
								{[2024, 2025, 2026].map((y) => <MenuItem key={y} value={y}>{y}/{y+1-2000}</MenuItem>)}
							</Select>
						</FormControl>

						<FormControl sx={{ minWidth: 150 }}>
							<InputLabel>Quarter</InputLabel>
							<Select value={quarter} label="Quarter" onChange={(e) => setQuarter(e.target.value)}>
								<MenuItem value={1}>Q1 (Apr-Jun)</MenuItem>
								<MenuItem value={2}>Q2 (Jul-Sep)</MenuItem>
								<MenuItem value={3}>Q3 (Oct-Dec)</MenuItem>
								<MenuItem value={4}>Q4 (Jan-Mar)</MenuItem>
							</Select>
						</FormControl>

						<Button variant="contained" onClick={handleFetchReports}>Generate Report</Button>
						{data && (
							<Button
								variant="outlined"
								startIcon={<Iconify icon="vscode-icons:file-type-excel" />}
								onClick={handleDownloadExcel}
							>
                            Download Excel
							</Button>
						)}
					</StyledRoot>
				</Card>

				{error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

				{data && (
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard title="Active Members" total={data.summary.active_members} icon="eva:people-fill" color="info" />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard title={`Total Joined (Q${quarter})`} total={currentJoinedTotal} color="success" icon="ant-design:user-add-outlined" />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard title="Idle Members" total={data.summary.idle_members} icon="eva:person-done-fill" color="warning" />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard title="Deletions/Tests" total={data.summary.test_deletions} icon="eva:trash-2-fill" color="error" />
						</Grid>
						{/* Detailed Table */}
						<Grid item xs={12}>
							<Card>
								<QuarterlyStatsTable data={data} selectedQuarter={quarter} />
							</Card>
						</Grid>
					</Grid>
				)}
			</Container>

			<Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
		</>
	);
}

// Small helper component for the top cards
function SummaryCard({ title, total, icon, color }) {
	return (
		<Card sx={{ p: 3, textAlign: "center", bgcolor: (theme) => theme.palette[color].lighter }}>
			<Iconify icon={icon} width={40} height={40} sx={{ mb: 1, color: (theme) => theme.palette[color].dark }} />
			<Typography variant="h3">{total || 0}</Typography>
			<Typography variant="subtitle2" sx={{ opacity: 0.72 }}>{title}</Typography>
		</Card>
	);
}